import React from "react";
import {WithTranslation, withTranslation} from "react-i18next";
import i18next from "i18next";
import {KatBox, KatButton, KatDivider, KatFileUpload, KatIcon, KatLabel, KatLink, KatSpinner} from "@amzn/katal-react";
import MetricsUtil from "src/utils/MetricsUtil";
import FileParser from "src/utils/FileParser";
import {ITEM_HEADER_TO_INDEX_MAP, PROGRAM_HEADER_TO_INDEX_MAP} from "src/model/TemplateUploadInputRecord";
import {bindActionCreators, Dispatch} from "redux";
import {connect} from "react-redux";
import ApplicationState from "src/model/ApplicationState";
import {setBulkProductsQueryClear, setBulkProductsQueryStart} from "src/actions/BulkReportQueryAction";
import {ProductInfo, ProgramInput, ServiceAddonFeeChosen} from "src/model/ProductMatch";
import {GetReportResponse} from "src/model/ProgramFees";
import RequestStatus from "src/model/RequestStatus";
import {CSVLink} from "react-csv";
import ErrorStatusComponent from "src/components/common/ErrorStatusComponent";
import {
    countryCurrencyUnitMap,
    countryDefaultCategoryTRexIdMap,
    defaultCountryCode,
    dimensionUnitList,
    getCategoryTRexIdMapConfig,
    getCurrencyUnitConfig,
    getWeightUnitConfig, helpPageUrl, IndiaSpecialFeeCategoryTRexIdMap,
    PROGRAM_NAME_OF_FBA, PROGRAM_NAME_OF_MFN,
    programInventoryMap,
    programNameMap,
    weightUnitList
} from "src/config/Config";
import {newProductTemplate} from "src/template/NewProductTemplate";
import moment from 'moment';
import {transReport} from "src/utils/ReportUtil";

interface StateProps {
    bulkReportRes: GetReportResponse
}

interface OwnProps {
    readonly metricsUtil?: MetricsUtil
}

const mapStateToProps = (state: ApplicationState): StateProps => ({
    bulkReportRes: state.bulkReportRes
});

interface DispatchProps {
    readonly dispatchBulkProductsQueryStart: (countryCode: string, products: Array<ProductInfo>) => void,
    readonly dispatchBulkProductsQueryClear: () => void
}

const mapDispatchToProps = (dispatch: Dispatch) => (
    bindActionCreators({
        dispatchBulkProductsQueryStart: setBulkProductsQueryStart,
        dispatchBulkProductsQueryClear: setBulkProductsQueryClear
    }, dispatch)
);

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = StateProps & OwnProps & DispatchProps & WithTranslation;

interface State {
    selectedFile: File,
    showUploadFileBlock: boolean,
    isProcessFileDisabled: boolean,
    isFileHasError: boolean
}

const initializedFile = new File([""], "");

export class TemplateUploadComponent extends React.Component<Props, State> {
    protected readonly metricsUtil: MetricsUtil;

    constructor(props: Props) {
        super(props);
        this.state = {
            selectedFile: initializedFile,
            showUploadFileBlock: true,
            isProcessFileDisabled: true,
            isFileHasError: false
        }

        if (this.props.metricsUtil) {
            this.metricsUtil = this.props.metricsUtil;
        } else {
            this.metricsUtil = MetricsUtil.getDefaultMetricUtil();
        }

        this.handleFilesAttached = this.handleFilesAttached.bind(this);
        this.handleFilesReplaced = this.handleFilesReplaced.bind(this);
        this.handleFilesRemoved = this.handleFilesRemoved.bind(this);
        this.handleProcessFile = this.handleProcessFile.bind(this);
        this.handleProcessAnotherFile = this.handleProcessAnotherFile.bind(this);
        this.handleCancelReportRequest = this.handleCancelReportRequest.bind(this);
        this.getParsedTxtData = this.getParsedTxtData.bind(this);
        this.getParsedTxtError = this.getParsedTxtError.bind(this);
        this.publishCounter = this.publishCounter.bind(this);
    }

    handleFilesAttached(event: KatFileUpload.FilesAttachedEvent) {
        this.setState({selectedFile: event.detail.files[0], isProcessFileDisabled: false});
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("uploadFileAttached", "click");
        }
    }

    handleFilesReplaced(event: KatFileUpload.FilesReplacedEvent) {
        this.setState({selectedFile: event.detail.newFiles[0], isProcessFileDisabled: false});
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("uploadFileReplaced", "click");
        }
    }

    handleFilesRemoved(event: KatFileUpload.FilesRemovedEvent) {
        this.setState({selectedFile: initializedFile, isProcessFileDisabled: true});
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("uploadFileRemoved", "click");
        }
    }

    handleProcessFile() {
        this.setState({showUploadFileBlock: false});
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("processFile", "click");
        }
        FileParser.parseTxt(this.state.selectedFile, this.getParsedTxtData, this.getParsedTxtError);
    }

    handleProcessAnotherFile() {
        this.setState({showUploadFileBlock: true, selectedFile: initializedFile, isProcessFileDisabled: true, isFileHasError: false});
        this.props.dispatchBulkProductsQueryClear();
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("processAnotherFile", "click");
        }
    }

    handleCancelReportRequest() {
        this.setState({showUploadFileBlock: true, selectedFile: initializedFile, isProcessFileDisabled: true, isFileHasError: false});
        this.props.dispatchBulkProductsQueryClear();
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("cancelProcessFile", "click");
        }
    }

    publishCounter(method: string, counter: string) {
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter(method, counter);
        }
    }

    getProductTRexId(feeCategory: string, countryCode: string) {
        const {t} = this.props;
        const categoryTRexIdMap = getCategoryTRexIdMapConfig(countryCode);
        // dynamic translation, if user change locale and use original template, it will fail.
        let categoriesMap = Object.keys(categoryTRexIdMap).reduce((acc, elem) => {
            acc[t(categoryTRexIdMap[Number(elem)])] = elem;
            return acc;
        }, {} as Record<string, string>);
        if (!(feeCategory in categoriesMap)) {
            if (feeCategory in IndiaSpecialFeeCategoryTRexIdMap) {
                return IndiaSpecialFeeCategoryTRexIdMap[feeCategory];
            } else {
                return countryDefaultCategoryTRexIdMap[countryCode].toString();
            }
        } else {
            return categoriesMap[feeCategory];
        }
    }

    getInputValue(originalValue: string, defaultValue: number): number {
        let processedNumber = originalValue.replace(/\d+\.\d+/g, "$&");
        return processedNumber.length != 0 ? parseFloat(processedNumber) : defaultValue;
    }

    getInventoryPlacedIn(program: string, inventoryInput: string, countryCode: string): string {
        if (!(program in programInventoryMap)) {
            return countryCode;
        }

        let inventoryList = programInventoryMap[program];
        if (inventoryInput in inventoryList) {
            return inventoryInput;
        } else {
            return inventoryList[0];
        }
    }

    getProgram(program: string, index: number): string {
        if (program == "") {
            return program;
        }

        const {t} = this.props;

        // translated program name => program ("Core", "MFN", etc.)
        let programStringNameMap = Object.keys(programNameMap).reduce((ret, key) => {
            ret[t(programNameMap[key])] = key;
            return ret;
        }, {} as Record<string, string>);

        return program in programStringNameMap ? programStringNameMap[program] : (index == 1 ? PROGRAM_NAME_OF_FBA : PROGRAM_NAME_OF_MFN);
    }

    getProgramInput(program: string, inventoryInput: string, itemData: string[], countryCode: string): ProgramInput {
        const inventoryPlace = this.getInventoryPlacedIn(program, inventoryInput, countryCode);

        return {
            marketplace: countryCode,
            inventoryPlace: inventoryPlace,
            averageInventoryUnitsStored: this.getInputValue(itemData[ITEM_HEADER_TO_INDEX_MAP.averageInventoryUnitsStored], 1),
            estimatedMonthlyUnitsSold: this.getInputValue(itemData[ITEM_HEADER_TO_INDEX_MAP.estimatedMonthlyUnitsSold], 1),
            costOfGoodsSold: this.getInputValue(itemData[ITEM_HEADER_TO_INDEX_MAP.costOfGoodsSold], 0),
            miscellaneousCost: this.getInputValue(itemData[ITEM_HEADER_TO_INDEX_MAP.miscellaneousCost], 0),
            estimatedSales: this.getInputValue(itemData[ITEM_HEADER_TO_INDEX_MAP.estimatedSales], 0),
            sendToAmazon: this.getInputValue(itemData[ITEM_HEADER_TO_INDEX_MAP.shippingToAmazon], 0),
            shippingCharge: this.getInputValue(itemData[ITEM_HEADER_TO_INDEX_MAP.mfnDeliveryCharge], 0),
            fulfillCost: this.getInputValue(itemData[ITEM_HEADER_TO_INDEX_MAP.mfnFulfillmentCostPerUnit], 0),
            monthlyStorageCostPerUnit: this.getInputValue(itemData[ITEM_HEADER_TO_INDEX_MAP.mfnMonthlyStorageCostPerUnit], 0),
            isPlacement: itemData[ITEM_HEADER_TO_INDEX_MAP.isInventoryPlacementChosen] == ServiceAddonFeeChosen.isChosen,
            isOpaqueBagging: itemData[ITEM_HEADER_TO_INDEX_MAP.isOpaqueBaggingChosen] == ServiceAddonFeeChosen.isChosen,
            isTape: itemData[ITEM_HEADER_TO_INDEX_MAP.isTapingChosen] == ServiceAddonFeeChosen.isChosen,
            isLabel: itemData[ITEM_HEADER_TO_INDEX_MAP.isLabellingChosen] == ServiceAddonFeeChosen.isChosen,
            isBagging: itemData[ITEM_HEADER_TO_INDEX_MAP.isPolyBaggingChosen] == ServiceAddonFeeChosen.isChosen,
            isBubbleWrap: itemData[ITEM_HEADER_TO_INDEX_MAP.isBubbleWrapChosen] == ServiceAddonFeeChosen.isChosen
        };
    }

    getSingleProductInfo(programData: string[], itemData: string[], countryCode: string): ProductInfo {
        const {t} = this.props;
        const isNonPeakSelected = programData[PROGRAM_HEADER_TO_INDEX_MAP.storageSeason] == t("SC_FBA_Calc_label_nonPeakPeriod");

        let productInfo: ProductInfo = {
            asin: itemData[ITEM_HEADER_TO_INDEX_MAP.asin],
            link: "",
            gl: "",
            tRexId: this.getProductTRexId(itemData[ITEM_HEADER_TO_INDEX_MAP.feeCategory], countryCode),
            title: itemData[ITEM_HEADER_TO_INDEX_MAP.productName],
            weightUnit: itemData[ITEM_HEADER_TO_INDEX_MAP.unitOfWeight] in weightUnitList ? itemData[ITEM_HEADER_TO_INDEX_MAP.unitOfWeight] : getWeightUnitConfig(countryCode),
            weight: this.getInputValue(itemData[ITEM_HEADER_TO_INDEX_MAP.itemPackageWeight], 1),
            dimensionUnit: itemData[ITEM_HEADER_TO_INDEX_MAP.unitOfDimension] in dimensionUnitList ? itemData[ITEM_HEADER_TO_INDEX_MAP.unitOfDimension] : dimensionUnitList[0],
            width: this.getInputValue(itemData[ITEM_HEADER_TO_INDEX_MAP.longestSide], 1),
            length: this.getInputValue(itemData[ITEM_HEADER_TO_INDEX_MAP.medianSide], 1),
            height: this.getInputValue(itemData[ITEM_HEADER_TO_INDEX_MAP.shortestSide], 1),
            // don't get currency input from user
            currency: getCurrencyUnitConfig(countryCode),
            price: this.getInputValue(itemData[ITEM_HEADER_TO_INDEX_MAP.price], 1),
            merchantSku: itemData[ITEM_HEADER_TO_INDEX_MAP.sku],
            fnsku: itemData[ITEM_HEADER_TO_INDEX_MAP.fnsku],
            isNonPeakSelected: isNonPeakSelected,
            programInputMap: new Map<string, ProgramInput>(),
        }

        const program1 = this.getProgram(programData[PROGRAM_HEADER_TO_INDEX_MAP.program1], 1);
        const program2 = this.getProgram(programData[PROGRAM_HEADER_TO_INDEX_MAP.program2], 2);

        if (program1 != "") {
            let programInput1 = this.getProgramInput(program1, programData[PROGRAM_HEADER_TO_INDEX_MAP.inventoryPlacedIn1], itemData, countryCode);
            productInfo.programInputMap?.set(program1, programInput1);
        }

        if (program2 != "") {
            let programInput2 = this.getProgramInput(program2, programData[PROGRAM_HEADER_TO_INDEX_MAP.inventoryPlacedIn2], itemData, countryCode);
            productInfo.programInputMap?.set(program2, programInput2);
        }

        return productInfo;
    };

    getParsedTxtError(error: any) {
        if (error.length > 0) {
            this.setState({isFileHasError: true});
        }
    }

    getParsedTxtData(programData: string[], itemData: string[][]) {
        const countryCode = programData[PROGRAM_HEADER_TO_INDEX_MAP.amazonStore] in countryCurrencyUnitMap ?
            programData[PROGRAM_HEADER_TO_INDEX_MAP.amazonStore] : defaultCountryCode;

        let products: Array<ProductInfo> = [];
        itemData.forEach((item: string[]) => {
            products.push(this.getSingleProductInfo(programData, item, countryCode));
        });
        this.props.dispatchBulkProductsQueryStart(countryCode, products);
    }

    getIsProcessingFileComponent() {
        const {t} = this.props;
        return (
            <div className="download-template-block">
                <div className="kat-spinner-process-file-box">
                    <KatSpinner size="small"/>
                </div>

                <div className="processing-file-label">
                    <KatLabel text={t("SC_FBA_Calc_bulk_processing_info", {X: 1})}/>
                </div>

                <div className="cancel-processing-button-box">
                    <KatButton label={t("SC_FBA_Calc_btn_cancel")} variant="secondary" onClick={this.handleCancelReportRequest}/>
                </div>
            </div>
        )
    }

    getErrorComponent() {
        const {t} = this.props;
        return (
            <>
                <ErrorStatusComponent description={this.state.isFileHasError ?
                    t("SC_FBA_Calc_bulk_info_invalid_file_type") : t("SC_FBA_Calc_bulk_info_error_in_processing")}/>
                {this.getProcessAnotherFileComponent()}
            </>
        )
    }

    getDownloadButtonComponent() {
        const reportKey = Object.keys(this.props.bulkReportRes.report);
        const reportData = this.props.bulkReportRes.report[reportKey[0]];
        const {t} = this.props;

        return (
            <div className="cancel-processing-button-box">
                <CSVLink data={reportData?.length > 0 ? transReport(reportData,t) : [[""]]}
                         filename={"RevCal_" + moment(new Date()).format("YYYY-MM-DD_HH-mm-ss")}>
                    <KatButton label={t("SC_FBA_Calc_bulk_btn_downloadReport")} className="download-template-button"
                               onClick={e => {
                                   this.publishCounter("template_unpload_download_report", "click")
                               }}/>
                </CSVLink>
            </div>
        )
    }

    getDoneProcessingFileComponent() {
        const {t} = this.props;
        return (
            <div className="download-template-block">
                <div className="check-icon-block">
                    <KatIcon name="check" size="small"/>
                </div>
                <div className="done-processing-text-block">
                    <KatLabel text={t("SC_FBA_Calc_bulk_info_doneProcessing")}/>
                </div>

                {this.getDownloadButtonComponent()}
            </div>
        )
    }

    getProcessAnotherFileComponent() {
        const {t} = this.props;
        return (
            <div className="process-another-file-button-box">
                <KatButton label={t("SC_FBA_Calc_bulk_btn_processanotherfile")} className="download-template-button"
                           onClick={this.handleProcessAnotherFile}/>
            </div>
        )
    }

    getProcessFileComponent() {
        return (
            // SC_FBA_Calc_bulk_info_invalid_file_type, SC_FBA_Calc_bulk_info_error_in_processing
            this.state.isFileHasError || this.props.bulkReportRes.requestStatus == RequestStatus.ERROR ?
                this.getErrorComponent()
                :
                <>
                    <KatBox variant="white" className="kat-box-process-file">
                        <div className="download-template-block">
                            <KatLabel text={this.state.selectedFile.name}/>

                            {
                                this.props.bulkReportRes.requestStatus == RequestStatus.SUCCESS ?
                                    this.getDoneProcessingFileComponent() : this.getIsProcessingFileComponent()
                            }

                        </div>
                    </KatBox>

                    <div>
                        {
                            this.props.bulkReportRes.requestStatus == RequestStatus.SUCCESS ?
                                this.getProcessAnotherFileComponent() :
                                <></>
                        }
                    </div>
                </>
        )
    }

    render() {
        const localHelpPageUrl = helpPageUrl + '?ref_=revecalc&language=' + i18next.language;
        const {t} = this.props;
        return (
            <div>
                <div className="download-template-block">
                    <KatLabel text={t("SC_FBA_Calc_bulk_templateStep1")} className="download-template-text"/>
                    <div className="download-template-button-box">
                        <KatLink href={newProductTemplate()}>
                            <KatButton variant="secondary" className="download-template-button"
                                       label={t("SC_FBA_Calc_bulk_btn_downloadTemplate")}/>
                        </KatLink>
                    </div>
                </div>

                <KatDivider variant="athens"/>

                <div className="upload-file-text-block">
                    <KatLabel text={t("SC_FBA_Calc_bulk_templateStep2")} className="download-template-text"/>
                    <div>
                        <KatLabel text={t("SC_FBA_Calc_bulk_uploadInfo")}
                                  className="upload-file-info"/>
                        <KatLink label={t("SC_FBA_Calc_bulk_link_learnMore")} href={localHelpPageUrl} variant="link"/>
                    </div>
                </div>

                {
                    this.state.showUploadFileBlock ?
                        <>
                            <div className="upload-file-button-box">
                                <KatFileUpload locale={i18next.language}
                                               accept={"text/plain"}
                                               maxSize={512000}
                                               onFilesAttached={this.handleFilesAttached}
                                               onFilesReplaced={this.handleFilesReplaced}
                                               onFilesRemoved={this.handleFilesRemoved}>
                                    <div slot="constraints-message">
                                        {t("SC_FBA_Calc_bulk_info_fileFormat", {X: "txt"})}
                                        <br/>
                                        {t("SC_FBA_Calc_bulk_info_fileMaxSize", {X: 500})}
                                    </div>
                                </KatFileUpload>
                            </div>

                            <div className="process-file-button-block">
                                <div className="process-file-button-box">
                                    <KatButton variant="primary" label={t("SC_FBA_Calc_bulk_btn_processFile")}
                                               disabled={this.state.isProcessFileDisabled}
                                               className="download-template-button" onClick={this.handleProcessFile}/>
                                </div>
                            </div>
                        </>
                        :
                        this.getProcessFileComponent()
                }

            </div>
        )
    }
}

export default withTranslation()(connector(TemplateUploadComponent));